/**
 * Klaviyo signup gift.
 *
 * Listens for Klaviyo's documented onsite-forms event (`klaviyoForms` on
 * window). Submitting the configured popup form only EARNS the gift: the gift
 * product (an unlisted 0-price SKU) goes into the cart once the cart holds a
 * paid product, straight away if it already does. It leaves again when the
 * last paid product is removed and comes back with the next one, so the gift
 * can't be checked out on its own from the storefront.
 *
 * Config comes from a JSON block rendered in theme.liquid
 * ([data-klaviyo-gift-config]: formId, variantId).
 *
 * Notes:
 * - The gift line carries `_klaviyo_gift` (NOT `_gift_for` — that property
 *   belongs to the deal gifts and gift-link-reconcile.js would remove the
 *   line as an orphan).
 * - One gift per browser. EARNED_KEY = waiting for a paid product or in the
 *   cart. ADDED_KEY = handed out. A gift that disappears while both are set
 *   was ordered or removed by the customer, so it is not added again.
 * - The drawer is refreshed via the theme's `apps:product-added-to-cart`
 *   event but NOT opened — the customer may be mid-popup (success message).
 * - Visitors who haven't earned the gift never trigger a /cart.js request.
 */
(function () {
  'use strict';

  var cfgEl = document.querySelector('[data-klaviyo-gift-config]');
  if (!cfgEl) return;

  var cfg;
  try {
    cfg = JSON.parse(cfgEl.textContent);
  } catch (e) {
    console.error('[klaviyo-gift] config parse error:', e);
    return;
  }
  if (!cfg || !cfg.variantId) return;

  // Comma-separated list of form IDs; empty = any form triggers the gift.
  var formIds = String(cfg.formId || '')
    .split(',')
    .map(function (s) {
      return s.trim();
    })
    .filter(Boolean);

  var DEBOUNCE_MS = 150;
  var CART_MUTATION_RE = /\/cart\/(add|change|update|clear)\.js(\?|$)/;
  var EARNED_KEY = 'nl_klaviyo_gift_earned';
  // One gift per browser regardless of which of the forms was submitted.
  var ADDED_KEY = 'nl_klaviyo_gift_added';

  var inFlight = false;
  var pending = false;
  var debounceTimer = null;

  function getFlag(key) {
    try {
      return localStorage.getItem(key) === '1';
    } catch (e) {
      return false;
    }
  }

  function setFlag(key, on) {
    try {
      if (on) localStorage.setItem(key, '1');
      else localStorage.removeItem(key);
    } catch (e) {
      /* private mode — the gift simply isn't remembered */
    }
  }

  function postCart(path, body) {
    return fetch(path, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then(function (res) {
      if (res.ok) return;
      return res
        .json()
        .catch(function () {
          return {};
        })
        .then(function (payload) {
          throw new Error(payload.description || payload.message || 'Cart request failed');
        });
    });
  }

  function isGiftLine(item) {
    return !!item.properties && item.properties._klaviyo_gift === 'Yes';
  }

  // Other €0 lines (Deal A / threshold gifts) don't count as a purchase.
  function isPaidLine(item) {
    return !isGiftLine(item) && item.final_line_price > 0;
  }

  async function reconcile() {
    if (!getFlag(EARNED_KEY)) return;
    if (inFlight) {
      pending = true;
      return;
    }
    inFlight = true;

    try {
      var cart = await fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) {
        return r.json();
      });
      var giftLine = cart.items.filter(isGiftLine)[0];
      var hasPaid = cart.items.some(isPaidLine);
      var mutated = false;

      if (giftLine && !hasPaid) {
        await postCart('/cart/change.js', { id: giftLine.key, quantity: 0 });
        setFlag(ADDED_KEY, false);
        mutated = true;
      } else if (giftLine && giftLine.quantity !== 1) {
        await postCart('/cart/change.js', { id: giftLine.key, quantity: 1 });
        mutated = true;
      } else if (giftLine) {
        setFlag(ADDED_KEY, true);
      } else if (getFlag(ADDED_KEY)) {
        setFlag(EARNED_KEY, false);
      } else if (hasPaid) {
        await postCart('/cart/add.js', {
          items: [{ id: cfg.variantId, quantity: 1, properties: { _klaviyo_gift: 'Yes' } }],
        });
        setFlag(ADDED_KEY, true);
        mutated = true;
      }

      if (mutated) {
        document.dispatchEvent(new CustomEvent('cart:updated'));
        document.dispatchEvent(new CustomEvent('apps:product-added-to-cart', { detail: {} }));
      }
    } catch (err) {
      console.error('[klaviyo-gift] error:', err);
    } finally {
      inFlight = false;
      if (pending) {
        pending = false;
        schedule();
      }
    }
  }

  function schedule() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(reconcile, DEBOUNCE_MS);
  }

  window.addEventListener('klaviyoForms', function (e) {
    var detail = e.detail || {};
    if (detail.type !== 'submit') return;
    if (formIds.length && formIds.indexOf(String(detail.formId)) === -1) return;
    if (getFlag(ADDED_KEY)) return;
    setFlag(EARNED_KEY, true);
    schedule();
  });

  document.addEventListener('cart:updated', schedule);

  // Catches every add / change, whichever theme or app code path made it.
  var originalFetch = window.fetch;
  window.fetch = function () {
    var args = arguments;
    var promise = originalFetch.apply(this, args);
    var url = args[0] instanceof Request ? args[0].url : String(args[0] || '');
    if (CART_MUTATION_RE.test(url)) {
      promise
        .then(function (res) {
          if (res && res.ok) schedule();
        })
        .catch(function () {});
    }
    return promise;
  };

  schedule();
})();
